import baseReducer, { BaseAction } from "./BaseReducer";
import { ActionType, ScreenRenderStatus } from "../util/CommonConstant";
import { FormState, SetDataAction } from "../interface/common";
import {
  PageUploadPDFModel,
  defaultPageUploadPDFModel,
  AudioWorkflowRow,
  AudioWorkflowsPagination,
} from "../model/PageUploadPDFModel";

export type PageUploadPDFAction =
  | SetDataAction<PageUploadPDFModel>
  | SetSelectedFileAction
  | SetSelectedFilesAction
  | SetUploadLogsAction
  | SetSimulationLogsAction
  | SetIsUploadingAction
  | SetFileUploadUUIDAction
  | SetIsProcessCompletedAction
  | SetSimulationAction
  | SetCompletedPdfNameAction
  | SetCompletedPdfNamesAction
  | SetProcessingLogsAction
  | SetAudioWorkflowsAction
  | SetAudioWorkflowsLoadingAction
  | SetAudioWorkflowsErrorAction
  | SetAudioWorkflowsPaginationAction
  | SetShowUploadModalAction;

export interface SetSelectedFileAction {
  type: ActionType.SET_SELECTED_FILE;
  payload: File | null;
}

export interface SetSelectedFilesAction {
  type: ActionType.SET_SELECTED_FILES;
  payload: File[];
}

export interface SetUploadLogsAction {
  type: ActionType.SET_UPLOAD_LOGS;
  payload: { timestamp: string; message: string }[] | null;
}

export interface SetSimulationLogsAction {
  type: ActionType.SET_SIMULATION_LOGS;
  payload: { timestamp: string; message: string }[] | null;
}

export interface SetIsUploadingAction {
  type: ActionType.SET_IS_UPLOADING;
  payload: boolean;
}

export interface SetFileUploadUUIDAction {
  type: ActionType.SET_FILE_UPLOAD_UUID;
  payload: string;
}

export interface SetIsProcessCompletedAction {
  type: ActionType.SET_IS_PROCESS_COMPLETED;
  payload: boolean;
}

export interface SetSimulationAction {
  type: ActionType.SET_SIMULATION;
  payload: boolean;
}

export interface SetCompletedPdfNameAction {
  type: ActionType.SET_COMPLETED_PDF_NAME;
  payload: string;
}

export interface SetCompletedPdfNamesAction {
  type: ActionType.SET_COMPLETED_PDF_NAMES;
  payload: string[];
}

export interface SetProcessingLogsAction {
  type: ActionType.SET_PROCESSING_LOGS;
  payload: Array<{ timestamp: string; message: string; level: string }>;
}

export interface SetAudioWorkflowsAction {
  type: ActionType.SET_AUDIO_WORKFLOWS;
  payload: AudioWorkflowRow[];
}

export interface SetAudioWorkflowsLoadingAction {
  type: ActionType.SET_AUDIO_WORKFLOWS_LOADING;
  payload: boolean;
}

export interface SetAudioWorkflowsErrorAction {
  type: ActionType.SET_AUDIO_WORKFLOWS_ERROR;
  payload: string | null;
}

export interface SetAudioWorkflowsPaginationAction {
  type: ActionType.SET_AUDIO_WORKFLOWS_PAGINATION;
  payload: AudioWorkflowsPagination;
}

export interface SetShowUploadModalAction {
  type: ActionType.SET_SHOW_UPLOAD_MODAL;
  payload: boolean;
}

export const INITIAL_PAGE_UPLOAD_PDF_STATE: FormState<PageUploadPDFModel> = {
  screenRenderStatus: ScreenRenderStatus.SUCCESS,
  errorMessage: "",
  uid: "",
  operationType: "CREATE" as any,
  data: defaultPageUploadPDFModel(),
};

const pageUploadPDFReducer = (
  state: FormState<PageUploadPDFModel> = INITIAL_PAGE_UPLOAD_PDF_STATE,
  action: BaseAction | PageUploadPDFAction
): FormState<PageUploadPDFModel> => {
  const baseState = baseReducer(state, action as BaseAction);

  switch (action.type) {
    case ActionType.SET_DATA:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          ...action.payload,
        },
      };
    case ActionType.SET_SELECTED_FILE:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          selectedFile: action.payload,
        },
      };
    case ActionType.SET_SELECTED_FILES:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          selectedFiles: action.payload,
        },
      };
    case ActionType.SET_UPLOAD_LOGS:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          uploadLogs: action.payload,
        },
      };
    case ActionType.SET_SIMULATION_LOGS:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          simulationLogs: action.payload,
        },
      };
    case ActionType.SET_IS_UPLOADING:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          isUploading: action.payload,
        },
      };
    case ActionType.SET_FILE_UPLOAD_UUID:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          fileUploadUUID: action.payload,
        },
      };
    case ActionType.SET_IS_PROCESS_COMPLETED:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          isProcessCompleted: action.payload,
        },
      };
    case ActionType.SET_SIMULATION:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          simulation: action.payload,
        },
      };
    case ActionType.SET_COMPLETED_PDF_NAME:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          completedPdfName: action.payload,
        },
      };
    case ActionType.SET_COMPLETED_PDF_NAMES:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          completedPdfNames: action.payload,
        },
      };
    case ActionType.SET_PROCESSING_LOGS:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          processingLogs: action.payload,
        },
      };
    case ActionType.SET_AUDIO_WORKFLOWS:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          audioWorkflows: action.payload,
        },
      };
    case ActionType.SET_AUDIO_WORKFLOWS_LOADING:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          audioWorkflowsLoading: action.payload,
        },
      };
    case ActionType.SET_AUDIO_WORKFLOWS_ERROR:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          audioWorkflowsError: action.payload,
        },
      };
    case ActionType.SET_AUDIO_WORKFLOWS_PAGINATION:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          audioWorkflowsPagination: action.payload,
        },
      };
    case ActionType.SET_SHOW_UPLOAD_MODAL:
      return {
        ...baseState,
        data: {
          ...baseState.data,
          showUploadModal: action.payload,
        },
      };
    default:
      return baseState;
  }
};

export default pageUploadPDFReducer;

